import React, { useEffect } from 'react';
import '../LandingPage.css';
import mbIcon from '../../assets/mb_icon.png';

interface AISchoolERPIndiaProps {
  onBack: () => void;
  onNavigateToLogin: () => void;
  onNavigateToContact: () => void;
}

const AISchoolERPIndia: React.FC<AISchoolERPIndiaProps> = ({ onBack, onNavigateToLogin, onNavigateToContact }) => {
  useEffect(() => {
    document.title = 'AI School Management Software for CBSE & ICSE Schools | My Learning Board';
    // Set meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Best AI-powered school ERP software in India for CBSE, ICSE & State Board schools. Features student management, fee collection, HR, WhatsApp integration. Trusted by 500+ schools. Book free demo!');
    }
    return () => {
      document.title = 'My Learning Board – AI-Powered School Management Software & ERP in India';
    };
  }, []);

  const features = [
    { icon: 'bi-translate', title: 'Multi-Language Support', desc: 'Support for Hindi, English, and regional languages for pan-India adoption' },
    { icon: 'bi-currency-rupee', title: 'INR Payment Gateway', desc: 'Razorpay integration for UPI, cards, net banking & wallets' },
    { icon: 'bi-building', title: 'CBSE/ICSE/State Board', desc: 'Pre-configured for all Indian education boards and curricula' },
    { icon: 'bi-whatsapp', title: 'WhatsApp Integration', desc: 'Native WhatsApp messaging for parent communication' },
    { icon: 'bi-shield-check', title: 'Data Localization', desc: 'Data stored in Indian servers compliant with IT Act' },
    { icon: 'bi-phone', title: 'Mobile-First Design', desc: 'Works seamlessly on budget smartphones across India' },
  ];

  const stats = [
    { number: '500+', label: 'Schools in India' },
    { number: '2L+', label: 'Students Managed' },
    { number: '15+', label: 'Indian States' },
    { number: '99.9%', label: 'Uptime' },
  ];

  return (
    <div className="landing-page min-vh-100">
      {/* Navigation */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm">
        <div className="container-fluid px-4">
          <a className="navbar-brand d-flex align-items-center" href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>
            <img src={mbIcon} alt="My Learning Board" className="mb-logo" />
            <span className="ms-2 fw-bold text-primary-custom">My Learning Board</span>
          </a>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary-custom" onClick={onBack}>
              <i className="bi bi-arrow-left me-2"></i>Home
            </button>
            <button className="btn btn-primary-custom text-white" onClick={onNavigateToLogin}>
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #FF9933 0%, #138808 100%)' }}>
        <div className="container-fluid px-4">
          <div className="row align-items-center py-5">
            <div className="col-lg-7">
              <span className="badge bg-white text-dark mb-3 px-3 py-2">
                <i className="bi bi-flag me-2"></i>Made for India
              </span>
              <h1 className="display-4 fw-bold mb-4">
                #1 AI-Powered School ERP for India
              </h1>
              <p className="lead mb-4 opacity-90">
                Purpose-built for Indian schools with CBSE, ICSE, and State Board support.
                Manage admissions, fees, attendance, and parent communication in Hindi & English.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <button className="btn btn-light btn-lg px-4" onClick={onNavigateToContact}>
                  <i className="bi bi-calendar-event me-2"></i>Book Free Demo
                </button>
                <button className="btn btn-outline-light btn-lg px-4" onClick={onNavigateToLogin}>
                  <i className="bi bi-play-circle me-2"></i>Start Free Trial
                </button>
              </div>
            </div>
            <div className="col-lg-5 text-center mt-4 mt-lg-0">
              <div className="bg-white rounded-4 p-4 shadow-lg">
                <div className="row g-3">
                  {stats.map((stat, idx) => (
                    <div key={idx} className="col-6">
                      <div className="text-center p-3">
                        <h2 className="fw-bold mb-0" style={{ color: '#138808' }}>{stat.number}</h2>
                        <small className="text-muted">{stat.label}</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why India Section */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">Built Specifically for Indian Schools</h2>
            <p className="text-muted lead">Understanding the unique needs of Indian education system</p>
          </div>
          <div className="row g-4">
            {features.map((feature, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                      style={{ width: '60px', height: '60px', backgroundColor: '#FF993320' }}>
                      <i className={`bi ${feature.icon} fs-4`} style={{ color: '#FF9933' }}></i>
                    </div>
                    <h5 className="card-title">{feature.title}</h5>
                    <p className="card-text text-muted">{feature.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Boards Supported */}
      <section className="py-5">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">All Indian Education Boards Supported</h2>
          </div>
          <div className="row justify-content-center g-4">
            {['CBSE', 'ICSE', 'State Boards', 'IB', 'Cambridge', 'Montessori'].map((board, idx) => (
              <div key={idx} className="col-6 col-md-4 col-lg-2">
                <div className="card border-0 shadow-sm text-center p-3">
                  <h5 className="mb-0" style={{ color: '#138808' }}>{board}</h5>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="container-fluid px-4 text-center">
          <h2 className="mb-3">Join 500+ Indian Schools Using My Learning Board</h2>
          <p className="lead opacity-75 mb-4">Start your digital transformation journey today</p>
          <button className="btn btn-light btn-lg px-5" onClick={onNavigateToContact}>
            <i className="bi bi-telephone me-2"></i>Request Callback
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer py-4">
        <div className="container-fluid px-4 text-center">
          <p className="mb-0 small">&copy; 2026 Omnix Digital Solutions. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AISchoolERPIndia;
